import type { Layout, TrackerPlacement } from "../types/layout";

/**
 * Stress-test mock: a 10 × 500 = 5000 tracker grid (10 rows × 500 cols)
 * inside a 4000 m × 1400 m boundary. Heavy load — real utility-scale
 * sites are typically 1k–10k trackers.
 *
 * "500×10" is read as 500 columns × 10 rows so the boundary stays
 * viewable; the alternative (500 rows × 10 cols) would be a 100m × 65km
 * sliver. Flip ROWS/COLS if you want the other shape.
 *
 * Spacing: GCR = 0.30 ⇒ PITCH_X = 2.278 / 0.30 ≈ 7.593 m. Y pitch is
 * TRACKER_L + 0.5 m gap = 130.168 m. Row-major numbering (t-0001 is the
 * bottom-left, t-5000 is the top-right).
 *
 * Used by the /projects/:projectId/layout/demo route and as the
 * fetch-failure fallback for real runs.
 */

const ROWS = 10;
const COLS = 500;
const TRACKER_W = 2.278;
const TRACKER_L = 129.668;
const PITCH_X = TRACKER_W / 0.3; // GCR = 0.30 → 7.593 m
const PITCH_Y = TRACKER_L + 0.5; // tracker_length + gap_y = 130.168 m
const SW_X_START = 10;
const SW_Y_START = 10;
const MODULES_PER_TRACKER = 108;

const trackers: TrackerPlacement[] = [];
let _n = 0;
for (let row = 0; row < ROWS; row++) {
  for (let col = 0; col < COLS; col++) {
    _n += 1;
    trackers.push({
      id: `t-${String(_n).padStart(4, "0")}`,
      sw_x: SW_X_START + col * PITCH_X,
      sw_y: SW_Y_START + row * PITCH_Y,
      modules_count: MODULES_PER_TRACKER,
      block: "108_INT",
    });
  }
}

export const MOCK_LAYOUT: Layout = {
  packing_config: {
    tracker_width: TRACKER_W,
    tracker_length: TRACKER_L,
    pitch_x: PITCH_X,
    gap_y: 0.5,
    gcr: 0.3,
    modules_per_tracker: MODULES_PER_TRACKER,
  },
  boundary: {
    // X extent: 10 + (500-1)*7.593 + 2.278 ≈ 3801 m → use 4000 m padding.
    // Y extent: 10 + (10-1)*130.168 + 129.668 ≈ 1311 m → use 1400 m padding.
    points: [
      { x: 0, y: 0, z: 0 },
      { x: 4000, y: 0, z: 0 },
      { x: 4000, y: 1400, z: 0 },
      { x: 0, y: 1400, z: 0 },
    ],
    closed: true,
  },
  trackers,
  // Stage 9 locked placement offset (mirrors backend/app/routes/layout.py).
  // Stage 10 reshape: map by block name; Stage 9 single-block runs only
  // populate ``108_INT``.
  placement_offset: { "108_INT": { x: 1.139, y: 60.1264 } },
};
